import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Table,
  Tag,
  Typography,
  Select,
  Space,
  Collapse,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import { SessionService } from "../../services/SessionService";
import {
  EvaluateResult,
  ChatCompletionRequest,
  ChatCompletionResponse,
  ChatCompletionMessage,
  ToolCall,
} from "../../model_gen/chat";
import { Application } from "../../model_gen/application";
import { Sentinel } from "../../model_gen/sentinel";
import { CompanyService } from "../../services/CompanyService";
import SentinelInfo from "./SentinelInfo";
import { Company } from "../../model_gen/company";

const { Title } = Typography;
const { Panel } = Collapse;

interface EventResult {
  eval_result: EvaluateResult;
  eval_request: ChatCompletionRequest;
  application: Application;
  company: Company;
  total_count: number;
  sentinel: Sentinel;
  chat_request: ChatCompletionRequest;
  chat_response: ChatCompletionResponse;
}

const EvaluationsOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const [results, setResults] = useState<EventResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(["FAULT"]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const pageSize = 10;

  const statusOptions = [
    { label: "FAULT", value: "FAULT" },
    { label: "PASS", value: "PASS" },
    { label: "NA", value: "NA" },
  ];

  const getResults = (offset: number, limit: number) => {
    SessionService.getAllEvaluationResults(
      offset,
      limit,
      selectedStatuses,
      selectedCompanyIds,
      setIsLoading,
      apiKey
    ).subscribe(
      (data: EventResult[]) => {
        setResults(data);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  useEffect(() => {
    CompanyService.getAffiliatedCompanies(null, apiKey).subscribe(
      (companiesData: Company[]) => {
        setCompanies(companiesData);
      },
      (error: any) => {
        console.error(error);
      }
    );
  }, [apiKey]);

  useEffect(() => {
    const offset = (currentPage - 1) * pageSize;
    getResults(offset, pageSize);
  }, [currentPage, selectedStatuses, selectedCompanyIds]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  const columns: ColumnsType<EventResult> = [
    {
      title: "Company",
      key: "company",
      render: (_, record) => record.company.company_name,
    },
    {
      title: "Application",
      key: "application",
      render: (_, record) => record.application.application_name,
    },
    {
      title: "Sentinel",
      key: "sentinel_name",
      dataIndex: ["eval_result", "sentinel_name"],
    },
    {
      title: "Intent",
      key: "application_action",
      dataIndex: ["chat_request", "action_type"],
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Tag
          color={
            record.eval_result.status === "FAULT"
              ? "error"
              : record.eval_result.status === "WARNING"
              ? "warning"
              : "success"
          }>
          {record.eval_result.status}
        </Tag>
      ),
    },
    {
      title: "Description",
      key: "description",
      dataIndex: ["eval_result", "description"],
    },
    {
      title: "Date",
      key: "date_created",
      render: (_, record) =>
        moment(record.eval_result.date_created).format("YYYY-MM-DD HH:mm:ss"),
    },
  ];

  const handleStatusChange = (values: string[]) => {
    setSelectedStatuses(values);
    setCurrentPage(1);
  };

  const formatTextOrJson = (content: any): string => {
    if (typeof content === "string") return content;
    try {
      return JSON.stringify(content, null, 2);
    } catch {
      return String(content);
    }
  };

  const renderToolCalls = (record: EventResult) => {
    return record.chat_response?.choices?.[0]?.message?.tool_calls?.map(
      (toolCall: ToolCall) => (
        <Row key={toolCall.id}>
          <pre style={{ whiteSpace: "pre-wrap" }}>
            <code>
              {toolCall.function?.name}({toolCall.function?.arguments})
            </code>
          </pre>
        </Row>
      )
    );
  };

  return (
    <div>
      <Row align="middle" style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Title level={4}>Evaluations</Title>
        </Col>
        <Col span={12} style={{ textAlign: "right" }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              disabled={isLoading}
              mode="multiple"
              allowClear
              style={{ width: "100%", maxWidth: 400, textAlign: "left" }}
              placeholder="Select status"
              onChange={handleStatusChange}
              value={selectedStatuses}
              options={statusOptions}
            />
            <Select
              mode="multiple"
              disabled={isLoading}
              style={{ width: "100%", maxWidth: 400, textAlign: "left" }}
              placeholder="Filter by company"
              onChange={(values: number[]) => {
                setSelectedCompanyIds(values);
                setCurrentPage(1);
              }}
              value={selectedCompanyIds}
              options={companies.map((company) => ({
                label: company.company_name,
                value: company.id,
              }))}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Space>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={results}
        rowKey={(record) => record.eval_result.id}
        expandable={{
          expandedRowRender: (record) => (
            <div>
              <Row gutter={16}>
                <Col span={12}>
                  <Collapse ghost>
                    <Panel
                      header={
                        <div style={{ fontWeight: 800, fontSize: "16px" }}>
                          Request{" "}
                          <span style={{ fontSize: 14, marginLeft: 8 }}>
                            {record.chat_request?.request_id}
                          </span>
                        </div>
                      }
                      key="1">
                      <Collapse
                        style={{ backgroundColor: "transparent" }}
                        bordered={false}
                        defaultActiveKey={
                          record.chat_request?.params?.messages?.length
                        }>
                        {record.chat_request?.params?.messages?.map(
                          (message: ChatCompletionMessage, index: number) => (
                            <Panel
                              key={index + 1}
                              header={
                                <div
                                  style={{
                                    color: "#828282",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    textTransform: "capitalize",
                                  }}>
                                  {message.role}
                                </div>
                              }
                              style={{
                                marginTop: 10,
                                backgroundColor: "white",
                                border: "none",
                              }}>
                              <span
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  whiteSpace: "pre-wrap",
                                }}>
                                {formatTextOrJson(message.content)}
                              </span>
                            </Panel>
                          )
                        )}
                      </Collapse>
                    </Panel>
                  </Collapse>
                </Col>
                <Col span={12}>
                  <Collapse ghost>
                    <Panel
                      header={
                        <div style={{ fontWeight: 800, fontSize: "16px" }}>
                          Response
                        </div>
                      }
                      key="1">
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {formatTextOrJson(
                          record.chat_response?.choices?.[0]?.message?.content
                        )}
                        {record.chat_response?.choices &&
                          record.chat_response?.choices?.length > 0 &&
                          record.chat_response?.choices[0]?.message
                            ?.tool_calls &&
                          record.chat_response?.choices[0]?.message?.tool_calls
                            ?.length > 0 && <Title level={5}>Tool Calls</Title>}
                        {renderToolCalls(record)}
                      </div>
                    </Panel>
                  </Collapse>
                </Col>
              </Row>

              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Collapse ghost>
                    <Panel
                      header={
                        <div style={{ fontWeight: 800, fontSize: "16px" }}>
                          Evaluation Request{" "}
                          <span style={{ fontSize: 14, marginLeft: 8 }}>
                            {record.eval_request?.request_id}
                          </span>
                        </div>
                      }
                      key="1">
                      <Collapse
                        style={{ backgroundColor: "transparent" }}
                        bordered={false}
                        defaultActiveKey={
                          record.eval_request?.params?.messages?.length
                        }>
                        {record.eval_request?.params?.messages?.map(
                          (message: ChatCompletionMessage, index: number) => (
                            <Panel
                              key={index + 1}
                              header={
                                <div
                                  style={{
                                    color: "#828282",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    textTransform: "capitalize",
                                  }}>
                                  {message.role}
                                </div>
                              }
                              style={{
                                marginTop: 10,
                                backgroundColor: "white",
                                border: "none",
                              }}>
                              <span
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  whiteSpace: "pre-wrap",
                                }}>
                                {formatTextOrJson(message.content)}
                              </span>
                            </Panel>
                          )
                        )}
                      </Collapse>
                    </Panel>
                  </Collapse>
                </Col>
              </Row>

              <SentinelInfo sentinel={record.sentinel} />
            </div>
          ),
        }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: results[0]?.total_count || 0,
          showSizeChanger: false,
        }}
        loading={isLoading}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default EvaluationsOverview;
